<template>
  <div class="settings-org-section">
    <SettingsSectionHeader
      title="Executives"
      subtitle="C-Level roster (read-only)"
      icon="pi-id-card"
    >
      <template #actions>
        <Button
          icon="pi pi-refresh"
          label="Refresh"
          size="small"
          severity="secondary"
          outlined
          :loading="loading"
          @click="loadExecutives"
        />
      </template>
    </SettingsSectionHeader>

    <div class="settings-org-section__body">
      <div v-if="loading && !executives.length" class="settings-org-section__state">
        <ProgressSpinner style="width: 2rem; height: 2rem" strokeWidth="4" />
      </div>

      <Message v-else-if="error" severity="error" :closable="false" class="settings-org-section__message">
        <div class="flex flex-col sm:flex-row sm:items-center gap-3">
          <span class="flex-1">{{ error }}</span>
          <Button label="Retry" size="small" severity="danger" outlined @click="loadExecutives" />
        </div>
      </Message>

      <template v-else>
        <Message
          v-if="scope !== 'global'"
          severity="info"
          :closable="false"
          class="settings-org-section__scope-note"
        >
          Showing executives for your department scope only.
        </Message>

        <div class="settings-org-section__toolbar">
          <span class="settings-org-section__count">
            {{ countLabel }}
          </span>
          <IconField class="settings-org-section__search">
            <InputIcon class="pi pi-search" />
            <InputText
              v-model="searchQuery"
              placeholder="Search executives..."
              class="w-full rounded-xl"
            />
          </IconField>
        </div>

        <div v-if="!filteredExecutives.length" class="settings-org-section__empty">
          <i class="pi pi-id-card settings-org-section__empty-icon" aria-hidden="true" />
          <p>{{ emptyMessage }}</p>
          <Button
            v-if="searchQuery"
            label="Clear search"
            size="small"
            severity="secondary"
            text
            @click="searchQuery = ''"
          />
        </div>

        <DataTable
          v-else
          :value="filteredExecutives"
          size="small"
          dataKey="role"
          sort-mode="single"
          removable-sort
        >
          <Column field="role" header="Role" sortable style="width: 6rem">
            <template #body="{ data }">
              <Tag :value="data.role" severity="secondary" />
            </template>
          </Column>
          <Column field="name" header="Name" sortable style="min-width: 10rem">
            <template #body="{ data }">
              <div class="flex items-center gap-2">
                <span
                  v-if="!showExecutiveAvatar(data)"
                  class="settings-executives-section__avatar-fallback crc-table-avatar crc-table-avatar--compact"
                  aria-hidden="true"
                >
                  {{ initials(data.name) }}
                </span>
                <img
                  v-else
                  :src="resolveAvatarUrl(data.avatar_url)"
                  :alt="data.name"
                  class="settings-executives-section__avatar crc-table-avatar crc-table-avatar--compact"
                  @error="markAvatarBroken(data.role)"
                />
                <span class="font-medium">{{ data.name || '—' }}</span>
              </div>
            </template>
          </Column>
          <Column field="position_name" header="Position" sortable style="min-width: 10rem">
            <template #body="{ data }">
              <span class="text-sm text-surface-600 dark:text-surface-400">
                {{ data.position_name || '—' }}
              </span>
            </template>
          </Column>
          <Column header="Branch headcount" style="width: 8rem">
            <template #body="{ data }">
              <span class="text-sm text-surface-600 dark:text-surface-400">
                {{ headcountLabel(data.role) }}
              </span>
            </template>
          </Column>
          <Column header="Actions" style="width: 5rem">
            <template #body="{ data }">
              <div class="crc-table-row-actions">
                <Button
                  v-if="data.user_id"
                  icon="pi pi-external-link"
                  size="small"
                  severity="secondary"
                  outlined
                  rounded
                  class="crc-table-action-btn"
                  v-tooltip.top="'Open employee profile'"
                  @click="openEmployee(data.user_id)"
                />
              </div>
            </template>
          </Column>
        </DataTable>
      </template>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import IconField from 'primevue/iconfield';
import InputIcon from 'primevue/inputicon';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Tag from 'primevue/tag';
import Message from 'primevue/message';
import ProgressSpinner from 'primevue/progressspinner';
import SettingsSectionHeader from '../SettingsSectionHeader.vue';
import { fetchOrganogramTree } from '@/api/organogramApi';
import { resolveAvatarUrl } from '@/utils/resolveAvatarUrl';
import { openEmployeeProfile } from '@/utils/openEmployeeProfile';

defineOptions({ name: 'SettingsExecutivesSection' });

defineProps({
  section: { type: Object, default: null },
});

const emit = defineEmits(['close-settings']);

const router = useRouter();

const executives = ref([]);
const branchHeadcounts = ref({});
const scope = ref('global');
const loading = ref(false);
const error = ref('');
const searchQuery = ref('');
const brokenAvatarKeys = ref({});

const showExecutiveAvatar = (data) =>
  Boolean(data?.avatar_url) && !brokenAvatarKeys.value[data.role];

const markAvatarBroken = (role) => {
  if (!role) return;
  brokenAvatarKeys.value = { ...brokenAvatarKeys.value, [role]: true };
};

const filteredExecutives = computed(() => {
  const query = searchQuery.value.trim().toLowerCase();
  const base = [...executives.value].sort((a, b) => {
    if (a.role === 'CEO') return -1;
    if (b.role === 'CEO') return 1;
    return String(a.role || '').localeCompare(String(b.role || ''), undefined, {
      sensitivity: 'base',
    });
  });
  if (!query) return base;
  return base.filter((item) => {
    const haystack = [
      item.role,
      item.name,
      item.nickname,
      item.position_name,
      headcountLabel(item.role),
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return haystack.includes(query);
  });
});

const countLabel = computed(() => {
  const total = executives.value.length;
  const suffix = total === 1 ? '' : 's';
  return `${total} executive${suffix}`;
});

const emptyMessage = computed(() => {
  if (searchQuery.value) return 'No executives match your search.';
  return 'No C-Level executives found in the organogram.';
});

const headcountLabel = (role) => {
  const count = branchHeadcounts.value[role];
  return count == null ? '—' : String(count);
};

const initials = (name) => {
  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

const loadExecutives = async () => {
  loading.value = true;
  error.value = '';
  brokenAvatarKeys.value = {};
  try {
    const data = await fetchOrganogramTree();
    executives.value = Array.isArray(data.executives) ? data.executives : [];
    scope.value = data.scope || 'global';
    const map = {};
    (data.branches || []).forEach((branch) => {
      if (branch?.role) {
        map[branch.role] = Number(branch.headcount || 0);
      }
    });
    branchHeadcounts.value = map;
  } catch (err) {
    error.value = err?.message || 'Failed to load executives.';
    executives.value = [];
    branchHeadcounts.value = {};
  } finally {
    loading.value = false;
  }
};

const openEmployee = (userId) => {
  if (userId == null) return;
  emit('close-settings');
  openEmployeeProfile(router, userId);
};

onMounted(() => {
  loadExecutives();
});
</script>

<style scoped>
.settings-org-section {
  display: flex;
  flex-direction: column;
  min-height: 0;
  flex: 1;
}

.settings-org-section__body {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 1rem 1.5rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.settings-org-section__state {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 12rem;
}

.settings-org-section__message,
.settings-org-section__scope-note {
  margin: 0;
}

.settings-org-section__toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.settings-org-section__count {
  font-size: 0.875rem;
  color: var(--crc-text-muted, var(--text-color-secondary));
}

.settings-org-section__search {
  width: min(100%, 18rem);
}

.settings-org-section__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  min-height: 10rem;
  border: 1px dashed var(--crc-panel-border, var(--surface-border));
  border-radius: 0.75rem;
  color: var(--crc-text-muted, var(--text-color-secondary));
  text-align: center;
}

.settings-org-section__empty-icon {
  font-size: 1.5rem;
}

.settings-org-section__empty p {
  margin: 0;
  font-size: 0.875rem;
}

.settings-executives-section__avatar {
  object-fit: cover;
}
</style>
